import React, { useState, useEffect, useRef } from 'react';
import { BOARD_SIZE } from '../../constants';
import player1Image from '../../assets/images/player_1.png';
import player2Image from '../../assets/images/player_2.png';

function GameOver() {
  const [showHighlightedCells, setShowHighlightedCells] = useState(false);
  const [isRightPlayerFaded, setIsRightPlayerFaded] = useState(false);
  const [isLeftPlayerPulsing, setIsLeftPlayerPulsing] = useState(false);
  const [showStar, setShowStar] = useState(false);
  const [showPointsAmount, setShowPointsAmount] = useState(false);

  const timeoutRef = useRef(null);

  useEffect(() => {
    const startAnimation = () => {
      // Reset all states
      setShowHighlightedCells(false);
      setIsRightPlayerFaded(false);
      setIsLeftPlayerPulsing(false);
      setShowStar(false);
      setShowPointsAmount(false);

      // Clear any existing timeouts
      if (timeoutRef.current) clearTimeout(timeoutRef.current);

      // Sequence of animations
      const sequence = [
        // Wait 0.5 second before starting
        () => new Promise(resolve => setTimeout(resolve, 500)),
        // Show highlighted cells
        () => {
          setShowHighlightedCells(true);
          return new Promise(resolve => setTimeout(resolve, 1000));
        },
        // Show points
        () => {
          setShowPointsAmount(true);
          return new Promise(resolve => setTimeout(resolve, 1000));
        },
        // Show winning animation
        () => {
          setIsRightPlayerFaded(true);
          setIsLeftPlayerPulsing(true);
          return new Promise(resolve => setTimeout(resolve, 500));
        },
        // Show star
        () => {
          setShowStar(true);
          setIsLeftPlayerPulsing(false);
          return new Promise(resolve => setTimeout(resolve, 2000));
        },
        // Fade out everything
        () => {
          setShowHighlightedCells(false);
          setShowPointsAmount(false);
          setShowStar(false);
          setIsRightPlayerFaded(false);
          return new Promise(resolve => setTimeout(resolve, 1000));
        }
      ];

      // Run the sequence
      const runSequence = async () => {
        for (const step of sequence) {
          await step();
        }
        // Start over
        timeoutRef.current = setTimeout(startAnimation, 500);
      };

      runSequence();
    };

    startAnimation();

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  // Create a board with stones and walls
  const board = Array(BOARD_SIZE).fill(null).map(() => Array(BOARD_SIZE).fill(null));

  // Place black stones on the left side, right next to the wall
  const wallColumn = Math.floor(BOARD_SIZE / 2);
  for (let i = 0; i < 4; i++) {
    board[i][wallColumn] = 'black';
  }

  // Place white stones on the right side, right next to the wall
  for (let i = 0; i < 4; i++) {
    board[i][wallColumn + 1] = 'white';
  }

  // Define vertical walls in the middle
  const verticalWalls = Array(BOARD_SIZE).fill(null).map(() => Array(BOARD_SIZE).fill(false));
  for (let i = 0; i < BOARD_SIZE; i++) {
    verticalWalls[i][wallColumn] = true;
  }

  return (
    <div className="flex justify-center w-full">
      <div className="flex items-center justify-between w-full max-w-2xl">
        {/* Player 1 (Black) */}
        <div className={`flex flex-col items-center z-10 relative transition-all duration-500 ${isLeftPlayerPulsing ? 'scale-110' : 'scale-100'}`}>
          <img src={player1Image} alt="Player 1" className="w-16 h-16 sm:w-24 sm:h-24 object-contain" />
          <div className={`text-xl sm:text-2xl font-bold text-white mt-2 transition-opacity duration-500 ${showPointsAmount ? 'opacity-100' : 'opacity-0'}`}>
            28
          </div>
          {/* Star */}
          <div className={`absolute -top-4 -right-4 w-8 h-8 sm:w-12 sm:h-12 transition-opacity duration-500 ${showStar ? 'opacity-100' : 'opacity-0'}`}>
            <svg viewBox="0 0 24 24" className="w-full h-full text-yellow-300">
              <path
                fill="currentColor"
                d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"
              />
            </svg>
          </div>
        </div>

        {/* Board */}
        <div className="relative rounded-lg p-2 sm:p-4" style={{ backgroundColor: '#e1c083' }}>
          <div
            className="grid gap-0 relative w-48 h-48 sm:w-80 sm:h-80"
            style={{
              gridTemplateColumns: `repeat(${BOARD_SIZE}, minmax(0, 1fr))`,
            }}
          >
            {board.map((row, rowIndex) =>
              row.map((cell, colIndex) => (
                <div
                  key={`${rowIndex}-${colIndex}`}
                  className="relative"
                >
                  {/* Grid lines */}
                  <div className="absolute inset-0 pointer-events-none">
                    {rowIndex < BOARD_SIZE - 1 && (
                      <div className="absolute bottom-0 left-0 right-0 h-[1px] sm:h-0.5 bg-[#99804a]" />
                    )}
                    {colIndex < BOARD_SIZE - 1 && (
                      <div className="absolute right-0 top-0 bottom-0 w-[1px] sm:w-0.5 bg-[#99804a]" />
                    )}
                  </div>

                  {/* Highlighted cells */}
                  <div className={`absolute inset-0 transition-opacity duration-1000 ${showHighlightedCells ? 'opacity-30' : 'opacity-0'}`}>
                    {colIndex <= wallColumn && (
                      <div className="absolute inset-0 bg-black" />
                    )}
                    {colIndex > wallColumn && (
                      <div className="absolute inset-0 bg-white" />
                    )}
                  </div>

                  {/* Stone */}
                  {cell && (
                    <div
                      className={`
                        absolute inset-1 sm:inset-2 rounded-full
                        ${cell === 'black'
                          ? 'bg-black shadow-sm shadow-white'
                          : 'bg-white shadow-sm shadow-black'
                        }
                      `}
                    />
                  )}

                  {/* Vertical walls */}
                  {colIndex < BOARD_SIZE - 1 && verticalWalls[rowIndex]?.[colIndex] && (
                    <div
                      className={`z-10 absolute right-0 top-0 bottom-0 w-1 sm:w-1.5 -mr-0.5 ${rowIndex === BOARD_SIZE - 1 ? 'bg-black' : (rowIndex % 2 === 0 ? 'bg-black' : 'bg-white')}`}
                    />
                  )}
                </div>
              ))
            )}
          </div>
        </div>

        {/* Player 2 (White) */}
        <div className={`flex flex-col items-center transition-opacity duration-500 ${isRightPlayerFaded ? 'opacity-30' : 'opacity-100'}`}>
          <img src={player2Image} alt="Player 2" className="w-16 h-16 sm:w-24 sm:h-24 object-contain" />
          <div className={`text-xl sm:text-2xl font-bold text-white mt-2 transition-opacity duration-500 ${showPointsAmount ? 'opacity-100' : 'opacity-0'}`}>
            21
          </div>
        </div>
      </div>
    </div>
  );
}

export default GameOver; 
